import { bloomberg } from '../theme/bloombergTheme';
import type { MacroGoodWhen } from '../services/api';

/**
 * Value and change formatting for macro series.
 *
 * Each catalogue unit means something specific about the stored number, and
 * getting this wrong is how a dashboard ends up printing "206000k claims":
 *
 *   %         a rate or spread, already in percent   -> 4.77%,  change in bp
 *   count     an actual count (ICSA)                 -> 206k
 *   k         FRED serves it in thousands            -> 7,271 -> 7.27M
 *   m         FRED serves it in millions             -> 0.426 -> 426k
 *   contracts CFTC net positioning, signed           -> -84.2k
 *   z         standard deviations of own history     -> +1.01σ
 *   idx | ratio | pts                                -> plain, 2dp
 */

const compact = (n: number, digits = 2): string => {
  const abs = Math.abs(n);
  const sign = n < 0 ? '-' : '';
  if (abs >= 1e12) return `${sign}${(abs / 1e12).toFixed(digits)}T`;
  if (abs >= 1e9) return `${sign}${(abs / 1e9).toFixed(digits)}B`;
  if (abs >= 1e6) return `${sign}${(abs / 1e6).toFixed(digits)}M`;
  if (abs >= 1e3) return `${sign}${(abs / 1e3).toFixed(abs >= 1e4 ? 0 : 1)}k`;
  return `${sign}${abs.toFixed(digits)}`;
};

export const formatMacroValue = (value: number | null | undefined, unit: string): string => {
  if (value === null || value === undefined || Number.isNaN(value)) return '—';
  switch (unit) {
    case '%':
      return `${value.toFixed(2)}%`;
    case 'count':
      return compact(value, 1);
    case 'k':
      return compact(value * 1e3);
    case 'm':
      return compact(value * 1e6);
    case 'contracts':
      return compact(value, 1);
    case 'z':
      return `${value >= 0 ? '+' : ''}${value.toFixed(2)}σ`;
    case 'ratio':
      return value.toFixed(2);
    default:
      return value.toFixed(2);
  }
};

/**
 * Change for one horizon, in the most readable form for that unit.
 * Rates and spreads move in basis points; everything else prefers a percentage
 * when the backend computed one, and falls back to the absolute move when a
 * percentage would be meaningless (a % of a %, positioning crossing zero).
 */
export const formatMacroChange = (
  change: { abs: number | null; pct: number | null } | undefined,
  unit: string
): string => {
  if (!change) return '—';
  if (unit === '%') {
    if (change.abs === null) return '—';
    const bp = Math.round(change.abs * 100);
    return `${bp >= 0 ? '+' : ''}${bp}bp`;
  }
  // A z-score change is an absolute move in sigma; a percentage of a value
  // that crosses zero is meaningless.
  if (unit === 'z') {
    if (change.abs === null) return '—';
    return `${change.abs >= 0 ? '+' : ''}${change.abs.toFixed(2)}σ`;
  }
  if (change.pct !== null && change.pct !== undefined) {
    return `${change.pct >= 0 ? '+' : ''}${change.pct.toFixed(1)}%`;
  }
  if (change.abs === null) return '—';
  return `${change.abs >= 0 ? '+' : ''}${compact(change.abs, 1)}`;
};

/** Signed direction of a change, independent of whether it is good news. */
export const changeDirection = (
  change: { abs: number | null; pct: number | null } | undefined
): 1 | -1 | 0 => {
  const v = change?.abs;
  if (v === null || v === undefined || v === 0) return 0;
  return v > 0 ? 1 : -1;
};

/**
 * Colour for a change, given what "good" means for the series.
 *
 * `good_when` is null for series with no benign direction (the dollar index,
 * M2 velocity, put/call). Those stay in primary ink: colouring them would
 * assert a judgement the data does not support. Status colour here always
 * ships alongside an arrow and the number itself, never as the sole signal.
 */
export const changeColor = (
  change: { abs: number | null; pct: number | null } | undefined,
  goodWhen: MacroGoodWhen
): string => {
  const dir = changeDirection(change);
  if (dir === 0 || !goodWhen) return bloomberg.text.primary;
  const isGood = goodWhen === 'up' ? dir > 0 : dir < 0;
  return isGood ? bloomberg.status.positive : bloomberg.status.negative;
};

/** Human label for the source tag shown on each row. */
export const SOURCE_LABEL: Record<string, string> = {
  fred: 'FRED',
  cftc: 'CFTC',
  dbnomics: 'DBnomics',
  oecd: 'OECD',
  cboe: 'CBOE',
  aaii: 'AAII',
};
